import { faArrowRotateLeft } from '@fortawesome/pro-regular-svg-icons'
import React, { useState } from 'react'

import {
  useMutationUnreserveBooking,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type ParameterID } from '@flecto/types'
import { Modal, useNotifications } from '@flecto/ui'

import { SectionCustomerNotificationEmail } from '../../../booking-customer'
import {
  ButtonBookingFlectoLinkCopy,
  CardBookingQuickDetails,
} from '../../../bookings'

export type ModalBookingActionRevertToNegotiationProps = {
  bookingId?: ParameterID
  onSuccess?: () => void
}

export function ModalBookingActionRevertToNegotiation({
  bookingId,
  onSuccess,
}: ModalBookingActionRevertToNegotiationProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_actions' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })

  const [sendCustomerEmail, setSendCustomerEmail] = useState(false)

  const { data: booking } = useQueryGetBookingById({ bookingId })

  const { mutateAsync: unreserveBooking, isLoading } =
    useMutationUnreserveBooking()

  const { showNotification } = useNotifications()

  const handleSubmit = async () => {
    try {
      await unreserveBooking({
        bookingId,
        options: { send_email: sendCustomerEmail },
      })
      showNotification({
        type: 'success',
        message: t('notification_action_revert_to_negotiation_success'),
      })
      onSuccess?.()
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_action_revert_to_negotiation_error'),
      })
    }
  }

  return (
    <Modal size='md'>
      <Modal.Close />
      <Modal.Icon
        icon={faArrowRotateLeft}
        color='purple'
        withCircleBackground
      />
      <Modal.Title>{t('modal_action_revert_to_negotiation_title')}</Modal.Title>
      <Modal.Subtitle>
        {t('modal_action_revert_to_negotiation_alert_description')}
      </Modal.Subtitle>
      <Modal.Content>
        <CardBookingQuickDetails
          bookingId={bookingId}
          previewNewBookingStatus='negotiating'
        />
        <SectionCustomerNotificationEmail
          mt='lg'
          customerId={booking?.customer?.internal_id}
          value={sendCustomerEmail}
          onChange={setSendCustomerEmail}
        />
      </Modal.Content>
      <Modal.Cancel />
      <Modal.Submit onClick={handleSubmit} loading={isLoading}>
        {tActions('confirm')}
      </Modal.Submit>
      <Modal.FooterExtra>
        <ButtonBookingFlectoLinkCopy booking={booking} />
      </Modal.FooterExtra>
    </Modal>
  )
}
